import { useEffect, useMemo, useRef, useState } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import StarRatings from "react-star-ratings";

import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import GoogleIcon from "@mui/icons-material/Google";
import KeyboardDoubleArrowDownRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowDownRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import {
  getActiveFeedbackQuestions,
  searchFeedbackEmployees,
  submitFeedbackResponse,
} from "api/Feedback";
import Button from "components/button";
import Iconify from "components/iconify";

import "./style.css";

const PRIMARY_COLOR = "#f15e22";

/** One Google review destination per branch country (NL / SR / CW). */
const GOOGLE_REVIEW_BY_COUNTRY = {
  NL: {
    country: "NL",
    flag: "circle-flags:nl",
    label: "Netherlands",
    title: "Loved your experience? Leave us a Google review!",
    subtitle: "Help others discover Satyam Holidays — it only takes a minute.",
    url: "https://g.page/r/CXZPS_-R1VNlEBM/review",
  },
  SR: {
    country: "SR",
    flag: "circle-flags:sr",
    label: "Suriname",
    title: "Leave a Google review for Satyam Holidays Suriname",
    subtitle: "Share your experience — it only takes a minute.",
    url: "https://search.google.com/local/writereview?placeid=ChIJaw_7W680CI0Rnj6KZnfZJgI",
  },
  CW: {
    country: "CW",
    flag: "circle-flags:cw",
    label: "Curaçao",
    title: "Leave a Google review for Satyam Holidays Curaçao",
    subtitle: "Share your experience — it only takes a minute.",
    url: "https://search.google.com/local/writereview?placeid=ChIJ_dh5WKQVsYURWdH0hLbmfHY",
  },
};

const normalizeBranchCountry = (raw) => {
  const code = String(raw || "")
    .trim()
    .toUpperCase();
  if (code === "CUR" || code === "CUW") return "CW";
  if (GOOGLE_REVIEW_BY_COUNTRY[code]) return code;
  return null;
};

const isAnswerEmpty = (q, value) => {
  if (value == null) return true;
  if (q.questionType === "MULTIPLE_CHOICE") {
    return !Array.isArray(value) || value.length === 0;
  }
  if (typeof value === "string") return value.trim() === "";
  return false;
};

const FeedbackPage = () => {
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [customerName, setCustomerName] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [employeeBranchCountry, setEmployeeBranchCountry] = useState(null);
  const [email, setEmail] = useState("");
  const [answers, setAnswers] = useState({});

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleEmployeeChange = (name, branchCountry = null) => {
    setEmployeeName(name);
    setEmployeeBranchCountry(normalizeBranchCountry(branchCountry));
  };

  useEffect(() => {
    let mounted = true;
    setLoadingQuestions(true);
    getActiveFeedbackQuestions().then((response) => {
      if (!mounted) return;
      if (response.ok && response.data?.items) {
        setQuestions(response.data.items);
      } else {
        setLoadError(
          "We could not load the survey. Please try again in a moment."
        );
      }
      setLoadingQuestions(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const updateAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const missingRequired = useMemo(() => {
    return questions
      .filter((q) => q.required)
      .some((q) => isAnswerEmpty(q, answers[q._id]));
  }, [questions, answers]);

  const handleSubmit = async () => {
    if (missingRequired) {
      setSubmitError("Please answer the required questions before submitting.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");

    const payloadAnswers = questions
      .map((q) => ({
        questionId: q._id,
        answer: answers[q._id] ?? null,
      }))
      .filter((a) => !(a.answer == null || a.answer === ""));

    const body = {
      customerName,
      employeeName,
      email,
      answers: payloadAnswers,
    };

    const response = await submitFeedbackResponse({ body });
    if (response.ok) {
      setSubmitted(true);
      setSubmitting(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSubmitError(
      response.data?.message ||
        "Something went wrong while submitting your feedback. Please try again."
    );
    setSubmitting(false);
  };

  if (loadingQuestions) {
    return (
      <div className="feedback-page">
        <Container maxWidth="md">
          <div className="feedback-loading">
            <CircularProgress sx={{ color: PRIMARY_COLOR }} />
            <p>Loading survey…</p>
          </div>
        </Container>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="feedback-page">
        <Container maxWidth="md">
          <Alert severity="error" sx={{ my: 4 }}>
            {loadError}
          </Alert>
        </Container>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="feedback-page">
        <Container maxWidth="md">
          <div className="feedback-thanks-card">
            <div className="feedback-thanks-icon">
              <CheckCircleOutlineRoundedIcon
                style={{ fontSize: 80, color: PRIMARY_COLOR }}
              />
            </div>
            <h1 className="feedback-thanks-title">Thank you!</h1>
            <p className="feedback-thanks-subtitle">
              Your feedback helps us serve you better. We truly appreciate the
              time you took to share your experience.
            </p>

            <div className="feedback-prize-callout">
              <div className="feedback-prize-callout__icon">
                <EmojiEventsRoundedIcon style={{ fontSize: 28 }} />
              </div>
              <div className="feedback-prize-callout__body">
                <span className="feedback-prize-callout__eyebrow">
                  One last step
                </span>
                <p className="feedback-prize-callout__title">
                  To qualify for the prizes, leave us a Google review
                </p>
                <p className="feedback-prize-callout__subtitle">
                  Tap the button below to open Google and share your experience
                  — it only takes a minute and secures your entry.
                </p>
              </div>
              <KeyboardDoubleArrowDownRoundedIcon
                className="feedback-prize-callout__arrow"
                style={{ fontSize: 28 }}
              />
            </div>

            <GoogleReviewCard branchCountry={employeeBranchCountry} />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="feedback-page">
      <Container maxWidth="md">
        <header className="feedback-hero">
          <span className="feedback-hero__eyebrow">Customer survey</span>
          <h1 className="feedback-hero__title">We value your feedback</h1>
          <p className="feedback-hero__subtitle">
            Every day we work hard to make your journey with Satyam Holidays
            even better than the last. Your honest feedback is what helps us
            grow, fine-tune our service, and create an experience you'll want
            to come back to. It only takes a couple of minutes — and it means
            the world to us. Thank you for helping us be better, for you.
          </p>
        </header>

        <section className="feedback-card">
          <div className="feedback-section">
            <h2 className="feedback-section__title">About you</h2>
            <div className="feedback-grid">
              <div className="feedback-field">
                <label htmlFor="customerName">Naam klant (your name)</label>
                <input
                  id="customerName"
                  type="text"
                  placeholder="Your full name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                />
              </div>
              <div className="feedback-field">
                <label htmlFor="employeeName">
                  Naam medewerker (employee who helped you)
                </label>
                <EmployeeAutocomplete
                  value={employeeName}
                  onChange={handleEmployeeChange}
                />
              </div>
              <div className="feedback-field feedback-field--full">
                <label htmlFor="email">Email (optional)</label>
                <input
                  id="email"
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="feedback-divider" />

          <div className="feedback-section">
            <h2 className="feedback-section__title">Your experience</h2>

            {questions.map((q, index) => (
              <QuestionRenderer
                key={q._id}
                index={index + 1}
                question={q}
                value={answers[q._id]}
                onChange={(value) => updateAnswer(q._id, value)}
              />
            ))}
          </div>

          {submitError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          ) : null}

          <Box mt={3}>
            <Button
              className="primary-color w-100 bigBtn"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Submit feedback"
              )}
            </Button>
          </Box>
        </section>

        <GoogleReviewCard branchCountry={employeeBranchCountry} />
      </Container>
    </div>
  );
};

/* -------------------------------------------------------------- */
/*  Sub-components                                                 */
/* -------------------------------------------------------------- */

const QuestionRenderer = ({ index, question, value, onChange }) => {
  return (
    <div className="feedback-question">
      <div className="feedback-question__header">
        <span className="feedback-question__index">{index}</span>
        <div>
          <p className="feedback-question__text">
            {question.questionText}
            {question.required ? (
              <span className="feedback-question__required"> *</span>
            ) : null}
          </p>
          {question.description ? (
            <p className="feedback-question__description">
              {question.description}
            </p>
          ) : null}
        </div>
      </div>

      <div className="feedback-question__body">
        {question.questionType === "NPS" ? (
          <NpsScale value={value} onChange={onChange} />
        ) : null}

        {question.questionType === "RATING" ? (
          <Box display="flex" justifyContent="center" my={1}>
            <StarRatings
              rating={Number(value) || 0}
              starRatedColor={PRIMARY_COLOR}
              numberOfStars={Number(question.maxRating) || 5}
              changeRating={(n) => onChange(n)}
              name={question._id}
              starDimension="36px"
              starSpacing="4px"
            />
          </Box>
        ) : null}

        {question.questionType === "SINGLE_CHOICE" ? (
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <RadioGroup
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
            >
              {question.options.map((opt) => (
                <FormControlLabel
                  key={opt}
                  value={opt}
                  control={
                    <Radio sx={{ "&.Mui-checked": { color: PRIMARY_COLOR } }} />
                  }
                  label={opt}
                />
              ))}
            </RadioGroup>
          </FormControl>
        ) : null}

        {question.questionType === "MULTIPLE_CHOICE" ? (
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <FormGroup>
              {question.options.map((opt) => {
                const selected = Array.isArray(value) && value.includes(opt);
                return (
                  <FormControlLabel
                    key={opt}
                    control={
                      <Checkbox
                        checked={selected}
                        onChange={(e) => {
                          const current = Array.isArray(value) ? [...value] : [];
                          if (e.target.checked) {
                            current.push(opt);
                          } else {
                            const i = current.indexOf(opt);
                            if (i >= 0) current.splice(i, 1);
                          }
                          onChange(current);
                        }}
                        sx={{ "&.Mui-checked": { color: PRIMARY_COLOR } }}
                      />
                    }
                    label={opt}
                  />
                );
              })}
            </FormGroup>
          </FormControl>
        ) : null}

        {question.questionType === "TEXT" ? (
          <input
            className="feedback-text-input"
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Type your answer…"
          />
        ) : null}

        {question.questionType === "LONG_TEXT" ? (
          <textarea
            className="feedback-text-input"
            rows={4}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Type your answer…"
          />
        ) : null}
      </div>
    </div>
  );
};

const NpsScale = ({ value, onChange }) => {
  const score = value == null ? null : Number(value);
  return (
    <div className="feedback-nps">
      <div className="feedback-nps__scale">
        {Array.from({ length: 11 }).map((_, n) => {
          const selected = score === n;
          return (
            <button
              type="button"
              key={n}
              className={`feedback-nps__btn ${
                selected ? "feedback-nps__btn--selected" : ""
              }`}
              onClick={() => onChange(n)}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className="feedback-nps__legend">
        <span>0 — Zeer onwaarschijnlijk</span>
        <span>10 — Zeer waarschijnlijk</span>
      </div>
    </div>
  );
};

const EmployeeAutocomplete = ({ value, onChange }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const term = (value || "").trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (term.length < 2) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const id = ++requestIdRef.current;
      const response = await searchFeedbackEmployees(term);
      if (id !== requestIdRef.current) return;
      if (response.ok && Array.isArray(response.data?.items)) {
        // Support both new `{ name, branchCountry }` and legacy string items.
        const normalized = response.data.items
          .map((item) => {
            if (typeof item === "string") {
              return { name: item, branchCountry: null };
            }
            if (item && typeof item.name === "string") {
              return {
                name: item.name,
                branchCountry: normalizeBranchCountry(item.branchCountry),
              };
            }
            return null;
          })
          .filter(Boolean);
        setOptions(normalized);
      } else {
        setOptions([]);
      }
      setLoading(false);
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  const select = (option) => {
    onChange(option.name, option.branchCountry);
    setOpen(false);
    setHighlight(-1);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (open && highlight >= 0 && options[highlight]) {
        e.preventDefault();
        select(options[highlight]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showDropdown =
    open && (value || "").trim().length >= 2 && (loading || options.length > 0);

  return (
    <div className="feedback-autocomplete" ref={wrapperRef}>
      <input
        id="employeeName"
        ref={inputRef}
        type="text"
        autoComplete="off"
        placeholder="Start typing an employee's name…"
        value={value || ""}
        onChange={(e) => {
          // Typing freely clears branch country until a suggestion is picked.
          onChange(e.target.value, null);
          setOpen(true);
          setHighlight(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
      />
      {loading ? (
        <span className="feedback-autocomplete__spinner">
          <CircularProgress size={16} sx={{ color: PRIMARY_COLOR }} />
        </span>
      ) : null}

      {showDropdown ? (
        <ul
          className="feedback-autocomplete__menu"
          role="listbox"
          onMouseDown={(e) => e.preventDefault()}
        >
          {loading && options.length === 0 ? (
            <li className="feedback-autocomplete__empty">Searching…</li>
          ) : null}
          {options.map((option, idx) => {
            const reviewMeta = option.branchCountry
              ? GOOGLE_REVIEW_BY_COUNTRY[option.branchCountry]
              : null;
            return (
              <li
                key={`${option.name}|${option.branchCountry || "unknown"}|${idx}`}
                role="option"
                aria-selected={idx === highlight}
                className={`feedback-autocomplete__item ${
                  idx === highlight ? "feedback-autocomplete__item--active" : ""
                }`}
                onMouseEnter={() => setHighlight(idx)}
                onClick={() => select(option)}
              >
                <span className="feedback-autocomplete__name">{option.name}</span>
                {reviewMeta ? (
                  <span className="feedback-autocomplete__branch">
                    <Iconify icon={reviewMeta.flag} width={16} />
                    {reviewMeta.label}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

const GoogleReviewCard = ({ branchCountry }) => {
  const country = normalizeBranchCountry(branchCountry);
  const review = country ? GOOGLE_REVIEW_BY_COUNTRY[country] : null;

  if (!review) {
    return (
      <div className="feedback-google-card feedback-google-card--pending">
        <div className="feedback-google-card__icon">
          <GoogleIcon style={{ fontSize: 36, color: "#9ca3af" }} />
        </div>
        <div className="feedback-google-card__body">
          <p className="feedback-google-card__title">
            Select the medewerker above to get the right Google review link
          </p>
          <p className="feedback-google-card__subtitle">
            We’ll show the Netherlands, Suriname, or Curaçao review page that
            matches their branch.
          </p>
        </div>
      </div>
    );
  }

  return (
    <a
      href={review.url}
      target="_blank"
      rel="noopener noreferrer"
      className="feedback-google-card"
    >
      <div className="feedback-google-card__icon feedback-google-card__icon--flag">
        <Iconify icon={review.flag} width={36} />
      </div>
      <div className="feedback-google-card__body">
        <p className="feedback-google-card__eyebrow">
          <GoogleIcon style={{ fontSize: 14, color: "#4285F4" }} />
          Google review · {review.label}
        </p>
        <p className="feedback-google-card__title">{review.title}</p>
        <p className="feedback-google-card__subtitle">{review.subtitle}</p>
      </div>
      <div className="feedback-google-card__cta">
        <span>Open</span>
        <OpenInNewIcon style={{ fontSize: 18 }} />
      </div>
    </a>
  );
};

export default FeedbackPage;
